import { Permission } from 'lib/permissions';
// This is a mapping of which backend permissions are toggled by which Permissions Table checkboxes.
export const permissionTableMapping: Record<string, Permission[]> = {
  // Admin
  'users.view': [Permission.UserView],
  'users.edit': [Permission.UserWrite, Permission.UserAvailabilityAllUsersWrite],

  'roles.view': [Permission.RoleView],

  'teams.view': [Permission.TeamView],
  'teams.edit': [Permission.TeamWrite],

  'organization-information.view': [Permission.OrganizationView],
  'organization-information.edit': [Permission.OrganizationWrite],
  'organization-billing.view': [Permission.InvoiceList, Permission.InvoiceView],
  'organization-user-sessions.view': [Permission.UserSessionsAllUsersView],
  'organization-user-sessions.edit': [Permission.UserSessionsWrite],
  'organization-user-audit-logs.view': [Permission.UserAuditLogsAllUsersView],
  'organization-view-mfa-settings.view': [Permission.UserMfaAllUsersView],

  'marketplace-connections-new.edit': [Permission.ExternalIntegrationCreate],
  'marketplace-connections-existing.view': [Permission.ExternalIntegrationView],
  'marketplace-connections-existing.edit': [Permission.ExternalIntegrationWrite],

  // Product
  // Inquiries
  'inquiry-details.create': [Permission.InquiryCreate],
  'inquiry-details.list': [Permission.InquiryList],
  'inquiry-details.view': [Permission.InquiryView],
  'inquiry-details.edit': [Permission.InquiryWrite],
  'inquiry-templates.create': [Permission.InquiryTemplateCreate],
  'inquiry-templates.view': [
    Permission.InquiryTemplateView,
    Permission.InquiryTemplateDocumentExtractionConfigView,
  ],
  'inquiry-templates.edit': [
    Permission.InquiryTemplateWrite,
    Permission.InquiryTemplateDocumentExtractionConfigEdit,
  ],

  // Verifications
  'verification-details.list': [Permission.VerificationList],
  'verification-details.view': [Permission.VerificationView],
  'verification-details.edit': [Permission.VerificationWrite],
  'verification-templates.view': [Permission.VerificationTemplateView],
  'verification-templates.edit': [Permission.VerificationTemplateWrite],

  // Accounts
  'account-details.create': [Permission.AccountCreate],
  'account-details.list': [Permission.AccountList],
  'account-details.view': [Permission.AccountView],
  'account-details.edit': [Permission.AccountWrite],
  'account-types.create': [Permission.AccountTypeCreate],
  'account-types.view': [Permission.AccountTypeView],
  'account-types.edit': [Permission.AccountTypeWrite],
  'account-actions.create': [Permission.AccountActionCreate],
  'account-actions.view': [Permission.AccountActionView],
  'account-actions.edit': [Permission.AccountActionWrite],

  'transaction-details.list': [Permission.TransactionList],
  'transaction-details.view': [Permission.TransactionView],
  'transaction-details.edit': [Permission.TransactionWrite],
  'transaction-types.create': [Permission.TransactionTypeCreate],
  'transaction-types.view': [Permission.TransactionTypeView],
  'transaction-types.edit': [Permission.TransactionTypeWrite],

  // Reports
  'report-details.list': [Permission.ReportList],
  'report-details.create': [Permission.ReportCreate],
  'report-details.view': [Permission.ReportAllView, Permission.ReportView],
  're-run.edit': [Permission.ReportWrite],
  'dismiss-matches.edit': [Permission.ReportIgnoreHitsWrite],
  'pause-or-resume.edit': [Permission.ReportPauseWrite],
  'report-templates.create': [Permission.ReportTemplateCreate],
  'report-templates.view': [Permission.ReportTemplateView],
  'report-templates.edit': [Permission.ReportTemplateWrite],

  // Cases
  'case-details.create': [Permission.CaseCreate],
  'case-details.list': [Permission.CaseList],
  'case-details.view': [Permission.CaseView],
  'case-details.edit': [Permission.CaseWrite],
  'case-templates.create': [Permission.CaseTemplateCreate],
  'case-templates.view': [Permission.CaseTemplateView],
  'case-templates.edit': [Permission.CaseTemplateWrite],
  'case-actions.edit': [
    Permission.CaseActionCreate,
    Permission.CaseActionView,
    Permission.CaseActionWrite,
  ],
  'assign.edit': [Permission.CaseAssign],
  'bulk-assign.edit': [Permission.CaseBulkAssign],
  'bulk-resolve.edit': [Permission.CaseBulkWrite],
  'case-analytics.view': [Permission.CaseAnalyticsView],
  'sar-details.create': [Permission.SARCreate],
  'sar-details.list': [Permission.SARList],
  'sar-details.view': [Permission.SARView],
  'sar-details.edit': [Permission.SARWrite],
  'sar-export.edit': [Permission.SARExport],
  'sar-file.edit': [Permission.SARFile],
  'sar-configuration-view.view': [Permission.SARConfigurationView],
  'sar-configuration-view.edit': [Permission.SARConfigurationWrite],
  'case-list-table.view': [Permission.TableViewView],
  'case-list-table.edit': [Permission.TableViewWrite],

  // Workflows
  'workflow-runs.view': [Permission.WorkflowRunList],
  'workflow-details.create': [Permission.WorkflowCreate],
  'workflow-details.view': [Permission.WorkflowView, Permission.WorkflowVersionList],
  'workflow-details.edit': [Permission.WorkflowWrite],

  // Graph
  'graph-queries.view': [Permission.GraphQueryView],
  'graph-queries.edit': [Permission.GraphQueryWrite],
  'graph-query-templates.create': [Permission.GraphQueryTemplateCreate],
  'graph-query-templates.view': [Permission.GraphQueryTemplateView],
  'graph-query-templates.edit': [Permission.GraphQueryTemplateWrite],
  'graph-configuration.view': [Permission.GraphView],
  'graph-configuration.edit': [Permission.GraphWrite],

  // Lists
  'list-details.create': [Permission.ListCreate],
  'list-details.view': [Permission.ListView],
  'list-details.edit': [Permission.ListWrite],
  'list-items.view': [Permission.ListItemView],
  'list-items.edit': [Permission.ListItemWrite],

  // Data
  'ssn.inquiries.view': [Permission.InquirySsnView],
  'ssn.verifications.view': [Permission.VerificationDatabaseSsnView],
  'ssn.accounts.view': [Permission.AccountSsnView],
  'ssn.reports.view': [Permission.ReportSsnView],
  'id-numbers.inquiries.view': [Permission.InquiryGovIdNumberView],
  'id-numbers.verifications.view': [Permission.VerificationGovIdNumberView],
  'id-numbers.accounts.view': [Permission.AccountIdNumbersView],
  'normalized_files.verifications.view': [Permission.DashboardVerificationsNormalizedView],

  // Development
  'api-keys.view': [Permission.ApiKeyView],
  'api-keys.edit': [Permission.ApiKeyWrite],
  'api-logs.view': [Permission.ApiLogList, Permission.ApiLogsView],
  'webhooks-details.view': [Permission.WebhookView],
  'webhooks-details.edit': [Permission.WebhookWrite],
  'webhooks-events.view': [Permission.WebhookEventView],
  'webhooks-events.edit': [Permission.WebhookEventWrite],
};
